import { Link, NavLink, useLocation, useNavigate, type Location as RouterLocation } from "react-router-dom"
import { Bell, Logo, RightArrow } from "../../components/svgCollection"
import { motion, AnimatePresence } from "motion/react"
import { appStore } from "../../store/appStore"
import { routeTitles } from "../../lib/routeTitles"
import { getUserFirstName, getUserInitials } from "../../lib/userDisplay"
import NotificationBadge from "../../components/NotificationBadge"
import { userStore } from "../../store/userStore"
import { unpaidOrderStore } from "../../store/unpaidOrderStore"
import logoMobile from "../../assets/logo-mk-2.svg"
import { isFilmPath, isPromoPath, stripCinemaSlug } from "../../lib/cinemaSlugs"
import { useCinemaHref } from "../../hooks/useCinemaHref"
import { afishaHref } from "../../lib/afishaTabs"

const DISPLAY = "'Tilda Sans', sans-serif"
const MONO = "'JetBrains Mono', monospace"
const BODY = "'Manrope', sans-serif"

function isHeaderNavActive(currentPath: string, to: string, end: boolean) {
    if (to.includes("?")) return currentPath === to
    const pathname = currentPath.split("?")[0]
    if (end) return currentPath === to
    return pathname === to || pathname.startsWith(`${to}/`)
}

export default function Header() {
    const location = useLocation()
    const navigate = useNavigate()
    /* Модальные роуты (location, auth, buy, notifications) рендерятся поверх фоновой
       страницы — хедер должен отражать фон, а не путь модалки. Разворачиваем цепочку
       backgroundLocation на случай модалки, открытой поверх другой модалки */
    let headerLocation = location
    let bg = (headerLocation.state as { backgroundLocation?: RouterLocation } | null)?.backgroundLocation
    while (bg) {
        headerLocation = bg
        bg = (headerLocation.state as { backgroundLocation?: RouterLocation } | null)?.backgroundLocation
    }
    const pathName = headerLocation.pathname
    const currentHeaderPath = `${headerLocation.pathname}${headerLocation.search}`
    const pathRest = stripCinemaSlug(pathName)
    const isStartPage = pathName === "/"
    const isFilmPage = isFilmPath(pathName)
    const isPromoPage = isPromoPath(pathName)
    const pageTitle = (headerLocation.state as { pageTitle?: string } | null)?.pageTitle
    const isMainPath =
        !isFilmPage &&
        !isPromoPage &&
        (pathName === "/" ||
            pathName === "/tickets" ||
            pathName === "/specials" ||
            pathName === "/profile" ||
            pathName.startsWith("/profile/") ||
            pathRest === "/afisha" ||
            pathRest === "/contacts")
    const title =
        pageTitle ||
        routeTitles[pathRest] ||
        routeTitles[pathName] ||
        (isFilmPage ? "Фильм" : isPromoPage ? "Акция" : "Мягкий кинотеатр")
    const href = useCinemaHref()
    const { user, unreadMessages } = userStore()
    const navLinks = [
        { to: href("/afisha"), label: "Главная", end: true },
        { to: afishaHref("Подборки"), label: "Подборки", end: false },
        { to: afishaHref("Акции"), label: "Акции", end: false },
        { to: href("/contacts"), label: "Контакты", end: true },
        ...(user ? [{ to: "/tickets", label: "Билеты", end: false }] : []),
    ]

    const hasUnpaidNotice = unpaidOrderStore(state => state.hasUnreadUnpaid())
    const notificationCount = unreadMessages + (hasUnpaidNotice ? 1 : 0)
    const { cinema, city, locationSelected } = appStore()

    const displayName = user ? getUserFirstName(user) : null
    const initials = user ? getUserInitials(user) : null

    return (
        <header
            className="fixed top-0 z-50 w-full"
            style={{
                background: "linear-gradient(to bottom, rgba(22,40,74,0.95) 0%, rgba(22,40,74,0.75) 80%, transparent 100%)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                paddingTop: "env(safe-area-inset-top)",
            }}>
            {/* ── Main header row (allowed paths + desktop always) ── */}
            <div
                className={`mx-auto ${isMainPath ? "flex" : "hidden md:flex"} h-14 max-w-7xl items-center justify-between px-5 md:h-20 lg:px-8`}>

                {/* ─── Mobile left: brand logo ─── */}
                <Link
                    to={locationSelected ? href("/afisha") : "/"}
                    className="flex items-center leading-none md:hidden">
                    <img src={logoMobile} alt="Мягкий кинотеатр" className="h-9 w-auto" />
                </Link>

                {/* ─── Mobile right: city badge + auth (как на десктопе) ─── */}
                <div className="flex items-center gap-2 md:hidden">
                    {/* City / cinema badge — скрыт на разводной, пока кинотеатр не выбран */}
                    {!(isStartPage && !locationSelected) && (
                        <NavLink
                            to="/location"
                            state={{ backgroundLocation: location }}
                            className="flex h-9 items-center gap-1.5 rounded-full border border-white/20 bg-white/[0.07] px-2.5 hover:bg-white/[0.13] transition-colors">
                            <svg width="10" height="13" viewBox="0 0 20 20" fill="none" className="shrink-0">
                                <path
                                    d="M10 2C7.24 2 5 4.24 5 7c0 3.75 5 11 5 11s5-7.25 5-11c0-2.76-2.24-5-5-5zm0 6.5A1.5 1.5 0 1 1 10 5a1.5 1.5 0 0 1 0 3z"
                                    fill="rgba(255,255,255,0.6)"
                                />
                            </svg>
                            <div className="flex max-w-[128px] flex-col gap-0.5">
                                {locationSelected ? (
                                    <>
                                        <span
                                            className="truncate text-[9px] leading-none text-white/50"
                                            style={{ fontFamily: MONO, fontWeight: 600, letterSpacing: "0.06em" }}>
                                            {city.name}
                                        </span>
                                        <span
                                            className="truncate text-[11px] leading-none text-white"
                                            style={{ fontFamily: MONO, fontWeight: 700 }}>
                                            {cinema.name}
                                        </span>
                                    </>
                                ) : (
                                    <span
                                        className="whitespace-nowrap text-[11px] leading-none text-white"
                                        style={{ fontFamily: MONO, fontWeight: 700 }}>
                                        Выбрать кинотеатр
                                    </span>
                                )}
                            </div>
                        </NavLink>
                    )}

                    {/* Auth: до входа — «Войти», после — колокольчик уведомлений.
                        На лендинге без выбранного города «Войти» не показываем */}
                    {user ? (
                        <NavLink
                            to="/profile/notifications"
                            state={{ backgroundLocation: location }}
                            className="relative flex items-center justify-center size-9 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 transition-colors">
                            <Bell />
                            <NotificationBadge count={notificationCount} className="-top-1 -right-1" />
                        </NavLink>
                    ) : (
                        !(isStartPage && !locationSelected) && (
                            <button
                                type="button"
                                onClick={() => navigate("/auth", { state: { backgroundLocation: location } })}
                                className="flex h-9 items-center justify-center rounded-full px-4 text-[12px] text-white"
                                style={{
                                    background: "#e4206b",
                                    fontFamily: MONO,
                                    fontWeight: 700,
                                    letterSpacing: "0.03em",
                                    boxShadow: "0 0 18px rgba(228,32,107,0.4)",
                                }}>
                                Войти
                            </button>
                        )
                    )}
                </div>

                {/* ─── Desktop left: Logo ─── */}
                <div className="hidden items-center gap-5 md:flex">
                    <Link to={href("/afisha")} className="flex items-center leading-none">
                        <Logo />
                    </Link>
                </div>

                {/* ─── Desktop center: pill nav (скрыта на разводной) ─── */}
                {!isStartPage && (
                    <nav className="hidden items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.06] p-1.5 md:flex">
                        {navLinks.map(({ to, label, end }) => {
                            const isActive = isHeaderNavActive(currentHeaderPath, to, end)

                            return (
                                <Link key={to} to={to} className="relative">
                                    <div
                                        className="relative rounded-full px-6 py-3 text-[14px]"
                                        style={{
                                            fontFamily: MONO,
                                            fontWeight: 700,
                                            color: isActive ? "#fff" : "rgba(255,255,255,0.5)",
                                            transition: "color 0.18s",
                                        }}>
                                        <AnimatePresence>
                                            {isActive && (
                                                <motion.div
                                                    key="pill"
                                                    initial={{ opacity: 0, scale: 0.88 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    exit={{ opacity: 0, scale: 0.88 }}
                                                    transition={{ duration: 0.18, ease: "easeOut" }}
                                                    className="absolute inset-0 rounded-full"
                                                    style={{
                                                        background: "#349be6",
                                                        boxShadow: "0 0 16px rgba(52,155,230,0.5)",
                                                    }}
                                                />
                                            )}
                                        </AnimatePresence>
                                        <span className="relative z-10">{label}</span>
                                        {to === "/tickets" && hasUnpaidNotice && (
                                            <NotificationBadge count={1} className="-top-0.5 -right-0.5" />
                                        )}
                                    </div>
                                </Link>
                            )
                        })}
                    </nav>
                )}

                {isStartPage && <div className="hidden md:block flex-1" />}

                {/* ─── Desktop right: City badge + Auth ─── */}
                <div className="hidden md:flex items-center gap-2">
                    {/* City / cinema badge — скрыт на разводной, пока кинотеатр не выбран */}
                    {!(isStartPage && !locationSelected) && (
                        <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                            <NavLink
                                to="/location"
                                state={{ backgroundLocation: location }}
                                className="flex h-[52px] items-center gap-2.5 rounded-full border border-white/20 bg-white/[0.07] px-4 hover:bg-white/[0.13] transition-colors">
                                <svg width="11" height="14" viewBox="0 0 20 20" fill="none">
                                    <path
                                        d="M10 2C7.24 2 5 4.24 5 7c0 3.75 5 11 5 11s5-7.25 5-11c0-2.76-2.24-5-5-5zm0 6.5A1.5 1.5 0 1 1 10 5a1.5 1.5 0 0 1 0 3z"
                                        fill="rgba(255,255,255,0.6)"
                                    />
                                </svg>
                                <div className="flex max-w-[180px] flex-col gap-1">
                                    {locationSelected ? (
                                        <>
                                            <span
                                                className="truncate text-[11px] leading-none text-white/50"
                                                style={{ fontFamily: MONO, fontWeight: 600, letterSpacing: "0.06em" }}>
                                                {city.name}
                                            </span>
                                            <span
                                                className="truncate text-[14px] leading-none text-white"
                                                style={{ fontFamily: MONO, fontWeight: 700 }}>
                                                {cinema.name}
                                            </span>
                                        </>
                                    ) : (
                                        <span
                                        className="whitespace-nowrap text-[14px] leading-none text-white"
                                            style={{ fontFamily: MONO, fontWeight: 700 }}>
                                            Выбрать кинотеатр
                                        </span>
                                    )}
                                </div>
                            </NavLink>
                        </motion.div>
                    )}

                    {/* Auth area */}
                    {user ? (
                        <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                            <Link
                                to="/profile"
                                className="relative flex h-[52px] items-center gap-3 rounded-full border border-white/20 bg-white/[0.07] px-4 hover:bg-white/[0.13] transition-colors">
                                <div className="relative shrink-0">
                                    <div
                                        className="flex size-9 items-center justify-center rounded-full text-[12px] text-white"
                                        style={{ background: "#e4206b", fontFamily: MONO, fontWeight: 700 }}>
                                        {initials ?? "👤"}
                                    </div>
                                    <NotificationBadge count={notificationCount} className="-top-1 -right-1" />
                                </div>
                                <div className="flex min-w-0 max-w-[160px] flex-col gap-1">
                                    <span
                                        className="truncate text-[11px] leading-none text-white/50"
                                        style={{ fontFamily: MONO, fontWeight: 600, letterSpacing: "0.04em" }}>
                                        Ваш профиль
                                    </span>
                                    <span
                                        className="truncate text-[14px] leading-none text-white"
                                        style={{ fontFamily: BODY, fontWeight: 600 }}>
                                        {displayName}
                                    </span>
                                </div>
                            </Link>
                        </motion.div>
                    ) : (
                        /* На лендинге без выбранного города «Войти» не показываем */
                        !(isStartPage && !locationSelected) && (
                            <motion.button
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                                onClick={() => navigate("/auth", { state: { backgroundLocation: location } })}
                                className="flex h-[52px] items-center justify-center rounded-full px-6 text-[13px] text-white"
                                style={{
                                    background: "#e4206b",
                                    fontFamily: MONO,
                                    fontWeight: 700,
                                    letterSpacing: "0.03em",
                                    boxShadow: "0 0 18px rgba(228,32,107,0.4)",
                                }}>
                                Войти
                            </motion.button>
                        )
                    )}
                </div>
            </div>

            {/* ── Mobile inner-page header (back + title) ── */}
            <div
                className={`mx-auto ${!isMainPath ? "flex md:hidden" : "hidden"} h-14 max-w-7xl items-center gap-2 px-5`}>
                <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => navigate(-1)}
                    className="flex shrink-0 items-center justify-center size-9 rounded-full border border-white/20 bg-white/10">
                    <div className="rotate-180">
                        <RightArrow />
                    </div>
                </motion.button>

                <div
                    className="min-w-0 flex-1 truncate text-center text-[17px] font-semibold tracking-tight text-white"
                    style={{ fontFamily: DISPLAY, fontWeight: 700 }}>
                    {title}
                </div>

                <div className="size-9 shrink-0 opacity-0" />
            </div>

            {/* Bottom border glow line */}
            <div
                className="h-px w-full"
                style={{ background: "linear-gradient(to right, transparent, rgba(52,155,230,0.3), transparent)" }}
            />
        </header>
    )
}
